import type { ServiceLink } from "@iec-id/shared/contracts/links";
import { Card, EmptyState, Icon, SectionCard, Skeleton } from "@iec-id/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isOffline } from "@/components/points/state";
import { DataDeletionDialog } from "@/components/services/data-deletion-dialog";
import { DisconnectDialog } from "@/components/services/disconnect-dialog";
import { SERVICES_NS } from "@/components/services/i18n";
import {
	EXTERNAL_SERVICES,
	ServiceIconTile,
	serviceDescription,
} from "@/components/services/service-icon";
import { ServiceOpenTile, ServiceTileName } from "@/components/services/service-open-tile";
import { ServiceRow } from "@/components/services/service-row";
import { api } from "@/lib/trpc";

export const Route = createFileRoute("/services")({
	component: ServicesSection,
});

type DialogTarget = { serviceId: string; name: string } | null;

/**
 * Раздел «Сервисы» (макет 3d, правая колонка).
 *
 * Инвариант №5: аккаунты впрок не создаются. Поэтому неподключённый сервис
 * здесь получает только ссылку «Открыть» — никакой кнопки «подключить»
 * не существует ни в UI, ни в контракте: связь регистрирует сам вход.
 */
function ServicesSection() {
	const { t } = useTranslation(SERVICES_NS);
	const [disconnectTarget, setDisconnectTarget] = useState<DialogTarget>(null);
	const [deletionTarget, setDeletionTarget] = useState<DialogTarget>(null);

	const links = api.links.list.useQuery({ includeDisconnected: true });
	const items: ServiceLink[] = links.data?.items ?? [];
	const connected = items.filter((link) => link.state.status === "active");
	const known = new Set(items.map((link) => link.serviceId));
	const available = EXTERNAL_SERVICES.filter((service) => !known.has(service.id));
	const disconnected = items.filter((link) => link.state.status === "disconnected");

	return (
		<div className="flex flex-col gap-1">
			<SectionCard
				title={t("title")}
				level="page"
				hint={t("hint")}
				hintTriggerLabel={t("common:action.hint")}
			>
				{links.isPending ? (
					<div className="flex flex-col gap-3">
						{[0, 1, 2].map((row) => (
							<div key={row} className="flex items-center gap-3.5">
								<Skeleton width={40} height={40} radius="control" />
								<span className="flex min-w-0 flex-grow flex-col gap-1.5">
									<Skeleton width="35%" height={16} />
									<Skeleton width="65%" height={14} />
								</span>
							</div>
						))}
					</div>
				) : null}

				{links.isError ? (
					<EmptyState
						title={t(isOffline() ? "offline.title" : "error.title")}
						description={t(isOffline() ? "offline.description" : "error.description")}
					/>
				) : null}

				{links.isSuccess && connected.length === 0 && disconnected.length === 0 ? (
					<EmptyState
						title={t("connected.emptyTitle")}
						description={t("connected.emptyDescription")}
					/>
				) : null}

				{connected.length > 0 || disconnected.length > 0 ? (
					<div className="flex flex-col">
						{[...connected, ...disconnected].map((link) => (
							<ServiceRow
								key={link.serviceId}
								link={link}
								onDisconnect={(serviceId, name) => setDisconnectTarget({ serviceId, name })}
								onDeleteData={(serviceId, name) => setDeletionTarget({ serviceId, name })}
							/>
						))}
					</div>
				) : null}
			</SectionCard>

			{available.length > 0 ? (
				<SectionCard title={t("available.title")}>
					<span className="t-meta text-fg-muted">{t("available.description")}</span>
					{/* Порог по ширине карточки, а не окна — см. `try-services-card.tsx`. */}
					<div className="@container">
						<div className="grid gap-1 @xl:grid-cols-2">
							{available.map((service) => (
								<ServiceOpenTile
									key={service.id}
									href={service.url}
									label={t("available.open")}
									padding="row"
									caption={serviceDescription(t, service.id)}
								>
									<ServiceIconTile serviceId={service.id} tone="card" />
									<ServiceTileName name={service.name} />
								</ServiceOpenTile>
							))}
						</div>
					</div>
				</SectionCard>
			) : null}

			{disconnected.some((link) => link.deletion?.state === "failed") ||
			connected.some((link) => link.deletion?.state === "failed") ? (
				<Card gap="row">
					<div className="flex items-center gap-2.5">
						<Icon name="TriangleAlert" size={20} />
						<span className="t-body">{t("deletion.failedHint")}</span>
					</div>
				</Card>
			) : null}

			{disconnectTarget ? (
				<DisconnectDialog
					open
					onOpenChange={(open) => !open && setDisconnectTarget(null)}
					serviceId={disconnectTarget.serviceId}
					serviceName={disconnectTarget.name}
				/>
			) : null}

			{deletionTarget ? (
				<DataDeletionDialog
					open
					onOpenChange={(open) => !open && setDeletionTarget(null)}
					serviceId={deletionTarget.serviceId}
					serviceName={deletionTarget.name}
				/>
			) : null}
		</div>
	);
}
